import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import corrugated from "../assets/products/corrugated.jpg";
import diecut from "../assets/products/diecut.jpg";
import heavyduty from "../assets/products/heavyduty.jpg";
import custom from "../assets/products/custom.jpg";

const products = [
  {
    title: "Corrugated Cartons",
    desc: "High-strength corrugated cartons designed for safe storage and transportation.",
    img: corrugated,
    tag: "Core Range",
    details: {
      overview:
        "Our corrugated cartons are manufactured using multi-ply fluted board for maximum compression strength and stacking performance. Suitable for FMCG, agriculture, pharma, and e-commerce industries.",
      features: [
        "Single, double & triple wall options",
        "Custom print-ready surfaces",
        "Moisture-resistant coatings available",
        "Sizes from small retail to bulk export",
      ],
      specs: "Flute types: A, B, C, E, F — GSM range: 100–450 — Max load: up to 500kg",
    },
  },
  {
    title: "Die-Cut Boxes",
    desc: "Precision die-cut packaging for retail and branded applications.",
    img: diecut,
    tag: "Retail & Branding",
    details: {
      overview:
        "Die-cut boxes are crafted with precision tooling for tight tolerances and clean lines. Ideal for product display, gifting, and shelf-ready packaging where presentation matters.",
      features: [
        "Full-colour CMYK & Pantone printing",
        "Window cut-outs available",
        "Matte, gloss & soft-touch finishes",
        "Flat-pack or pre-glued assembly",
      ],
      specs: "Min order: 500 units — Lead time: 7–14 days — Custom inserts on request",
    },
  },
  {
    title: "Heavy Duty Packaging",
    desc: "Reinforced solutions for industrial and heavy-weight products.",
    img: heavyduty,
    tag: "Industrial",
    details: {
      overview:
        "Built for demanding supply chains, our heavy-duty packaging withstands vibration, impact, and compression during long-haul transport and warehousing of industrial goods.",
      features: [
        "Triple-wall construction standard",
        "Pallet-optimised dimensions",
        "Internal brace & divider systems",
        "Export-compliant (ISPM-15 compatible lining)",
      ],
      specs: "Load capacity: 200–1000kg — Tested to ASTM D4169 — Custom engineering available",
    },
  },
  {
    title: "Custom Packaging",
    desc: "Tailor-made corrugated solutions built to your exact requirements.",
    img: custom,
    tag: "Bespoke",
    details: {
      overview:
        "From concept to production, our custom packaging service covers structural design, prototyping, and full-run manufacturing. We work closely with your team to deliver packaging that fits perfectly — functionally and visually.",
      features: [
        "In-house structural design team",
        "3D prototyping & sample approval",
        "Brand colour matching",
        "Low MOQ for new product launches",
      ],
      specs: "Turnaround: prototype in 3–5 days — Production from 250 units — NDA available",
    },
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Products() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  return (
    <section id="products" className="py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div {...fadeUp(0)} className="mb-16 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block w-8 h-px bg-brand-green" />
            <span className="text-brand-green text-xs uppercase tracking-[0.2em] font-semibold">What We Make</span>
            <span className="block w-8 h-px bg-brand-green" />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl mb-4">Our Products</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base leading-relaxed">
            Engineered corrugated packaging designed to meet diverse industry
            requirements with strength, precision, and reliability.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {products.map((item, index) => (
            <motion.div
              key={index}
              {...fadeUp(0.1 + index * 0.08)}
              className="group relative h-80 overflow-hidden rounded-2xl cursor-pointer shadow-sm"
              onClick={() => setSelected(item)}
            >
              <img
                src={item.img}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                style={{ transform: "scale(1.02)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500" />

              <div className="absolute top-4 left-4 z-10">
                <span className="bg-brand-green/90 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full">
                  {item.tag}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 z-10 p-6 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="font-heading text-2xl text-white mb-1 leading-tight">{item.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300 max-w-sm">
                  {item.desc}
                </p>
                <div className="mt-3 flex items-center gap-2 text-brand-lightGreen text-xs uppercase tracking-widest font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span>Learn More</span>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Modal ── */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              onClick={() => setSelected(null)}
            />

            <motion.div
              key="modal"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            >
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden pointer-events-auto max-h-[90vh] flex flex-col">

                {/* Image header */}
                <div className="relative h-52 overflow-hidden flex-shrink-0">
                  <img src={selected.img} alt={selected.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />

                  <button
                    onClick={() => setSelected(null)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/40 transition flex items-center justify-center"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  <div className="absolute bottom-4 left-6">
                    <span className="bg-brand-green/90 text-white text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full">
                      {selected.tag}
                    </span>
                    <h3 className="font-heading text-2xl text-white mt-2">{selected.title}</h3>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5 overflow-y-auto">
                  <p className="text-gray-600 leading-relaxed text-sm">{selected.details.overview}</p>

                  <div>
                    <h4 className="text-[10px] uppercase tracking-widest text-brand-green font-semibold mb-3">Key Features</h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {selected.details.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-xl px-4 py-3 text-xs text-gray-500 border border-gray-100">
                    <span className="font-semibold text-gray-700 uppercase tracking-widest text-[10px]">Specs — </span>
                    {selected.details.specs}
                  </div>

                  <button
                    onClick={() => { setSelected(null); navigate("/contact"); }}
                    className="w-full py-3 bg-brand-green text-white text-sm uppercase tracking-widest font-semibold rounded-xl hover:bg-brand-blue transition-colors duration-200"
                  >
                    Request a Quote
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}